import { supabase } from './supabase';
import { User, FollowRequest } from '../types';

// Follow/Unfollow for public accounts
export const followUser = async (followerId: string, followingId: string): Promise<void> => {
  const { error } = await supabase
    .from('follows')
    .insert([
      {
        follower_id: followerId,
        following_id: followingId,
      }
    ]);

  if (error) throw error;
};

export const unfollowUser = async (followerId: string, followingId: string): Promise<void> => {
  const { error } = await supabase
    .from('follows')
    .delete()
    .eq('follower_id', followerId)
    .eq('following_id', followingId);

  if (error) throw error;
};

// Follow requests for private accounts
export const sendFollowRequest = async (requesterId: string, requestedId: string): Promise<void> => {
  const { error } = await supabase
    .from('follow_requests')
    .insert([
      {
        requester_id: requesterId,
        requested_id: requestedId,
        status: 'pending'
      }
    ]);

  if (error) throw error;
};

export const cancelFollowRequest = async (requesterId: string, requestedId: string): Promise<void> => {
  const { error } = await supabase
    .from('follow_requests')
    .delete()
    .eq('requester_id', requesterId)
    .eq('requested_id', requestedId)
    .eq('status', 'pending');

  if (error) throw error;
};

export const acceptFollowRequest = async (requestId: string): Promise<void> => {
  // Get the request details
  const { data: request, error: requestError } = await supabase
    .from('follow_requests')
    .select('requester_id, requested_id')
    .eq('id', requestId)
    .single();

  if (requestError) throw requestError;

  // Create the follow relationship
  const { error: followError } = await supabase
    .from('follows')
    .insert([{
      follower_id: request.requester_id,
      following_id: request.requested_id
    }]);

  if (followError) throw followError;

  // Update request status
  const { error: updateError } = await supabase
    .from('follow_requests')
    .update({ status: 'accepted' })
    .eq('id', requestId);

  if (updateError) throw updateError;
};

export const rejectFollowRequest = async (requestId: string): Promise<void> => {
  const { error } = await supabase
    .from('follow_requests')
    .update({ status: 'rejected' })
    .eq('id', requestId);

  if (error) throw error;
};

// Check follow status
export const isFollowing = async (followerId: string, followingId: string): Promise<boolean> => {
  const { data, error } = await supabase
    .from('follows')
    .select('id')
    .eq('follower_id', followerId)
    .eq('following_id', followingId)
    .maybeSingle();

  if (error) throw error;
  return !!data;
};

export const hasRequestedToFollow = async (requesterId: string, requestedId: string): Promise<boolean> => {
  const { data, error } = await supabase
    .from('follow_requests')
    .select('id')
    .eq('requester_id', requesterId)
    .eq('requested_id', requestedId)
    .eq('status', 'pending')
    .maybeSingle();

  if (error) throw error;
  return !!data;
};

// Get follow counts
export const getFollowCounts = async (userId: string): Promise<{ followers: number; following: number }> => {
  const [followersResult, followingResult] = await Promise.all([
    supabase
      .from('follows')
      .select('*', { count: 'exact', head: true })
      .eq('following_id', userId),
    supabase
      .from('follows')
      .select('*', { count: 'exact', head: true })
      .eq('follower_id', userId)
  ]);

  return {
    followers: followersResult.count || 0,
    following: followingResult.count || 0,
  };
};

// Get followers list
export const getFollowers = async (userId: string, limit: number = 50): Promise<User[]> => {
  const { data, error } = await supabase
    .from('follows')
    .select(`
      *,
      follower:profiles!follows_follower_id_fkey (
        id,
        username,
        full_name,
        avatar_url,
        bio
      )
    `)
    .eq('following_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;

  return (data || []).map(follow => ({
    id: follow.follower.id,
    username: follow.follower.username,
    displayName: follow.follower.full_name || follow.follower.username,
    profileImage: follow.follower.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
    bio: follow.follower.bio || '',
    followerCount: 0,
    followingCount: 0,
    postCount: 0,
  }));
};

// Get following list
export const getFollowing = async (userId: string, limit: number = 50): Promise<User[]> => {
  const { data, error } = await supabase
    .from('follows')
    .select(`
      *,
      following:profiles!follows_following_id_fkey (
        id,
        username,
        full_name,
        avatar_url,
        bio
      )
    `)
    .eq('follower_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;

  return (data || []).map(follow => ({
    id: follow.following.id,
    username: follow.following.username,
    displayName: follow.following.full_name || follow.following.username,
    profileImage: follow.following.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
    bio: follow.following.bio || '',
    followerCount: 0,
    followingCount: 0,
    postCount: 0,
  }));
};

// Get pending follow requests
export const getPendingFollowRequests = async (userId: string): Promise<FollowRequest[]> => {
  const { data, error } = await supabase
    .from('follow_requests')
    .select(`
      *,
      requester:profiles!follow_requests_requester_id_fkey (
        id,
        username,
        full_name,
        avatar_url,
        bio
      )
    `)
    .eq('requested_id', userId)
    .eq('status', 'pending')
    .order('created_at', { ascending: false });

  if (error) throw error;

  return (data || []).map(request => ({
    id: request.id,
    requester_id: request.requester_id,
    requested_id: request.requested_id,
    status: request.status,
    created_at: request.created_at,
    requester: {
      id: request.requester.id,
      username: request.requester.username,
      displayName: request.requester.full_name || request.requester.username,
      profileImage: request.requester.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
      bio: request.requester.bio || '',
      followerCount: 0,
      followingCount: 0,
      postCount: 0,
    }
  }));
};

// Get mutual followers
export const getMutualFollowers = async (userId1: string, userId2: string): Promise<User[]> => {
  const { data, error } = await supabase
    .from('follows')
    .select(`
      follower_id,
      profiles!follows_follower_id_fkey (
        id,
        username,
        full_name,
        avatar_url,
        bio
      )
    `)
    .eq('following_id', userId1)
    .in('follower_id', 
      supabase
        .from('follows')
        .select('follower_id')
        .eq('following_id', userId2)
    );

  if (error) throw error;

  return (data || []).map(follow => ({
    id: follow.profiles.id,
    username: follow.profiles.username,
    displayName: follow.profiles.full_name || follow.profiles.username,
    profileImage: follow.profiles.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
    bio: follow.profiles.bio || '',
    followerCount: 0,
    followingCount: 0,
    postCount: 0,
  }));
};

// Remove follower
export const removeFollower = async (userId: string, followerId: string): Promise<void> => {
  const { error } = await supabase
    .from('follows')
    .delete()
    .eq('follower_id', followerId)
    .eq('following_id', userId);

  if (error) throw error;
};

// Block user (removes follow relationship and prevents future follows)
export const blockUser = async (blockerId: string, blockedId: string): Promise<void> => {
  // Remove any existing follow relationships
  await Promise.all([
    unfollowUser(blockerId, blockedId),
    unfollowUser(blockedId, blockerId)
  ]);

  // Cancel any pending follow requests
  await Promise.all([
    cancelFollowRequest(blockerId, blockedId),
    cancelFollowRequest(blockedId, blockerId)
  ]);

  // Note: In a full implementation, you'd also create a blocks table
  // For now, we just remove the relationships
};